import { injectable, inject } from 'inversify';
import Debug from 'debug';
import { CommandHandler, CommandHandlerFactorySymbol, Command } from '../command';
import { Query, QueryHandlerFactorySymbol, QueryHandler } from '../query';
import { CommandQueue } from '@/app/shared/command-queue';

const debug = Debug('store-command-executor');

@injectable()
export class StoreCommandExecutor {

  private commandQueue: CommandQueue

  constructor(
    @inject(CommandHandlerFactorySymbol)
    private handlerFactory: (name: string) => CommandHandler<Command<string, unknown>>,
    @inject(QueryHandlerFactorySymbol)
    private queryHandlerFactory: <T extends string, TPayload, TResult>(name: string) => QueryHandler<Query<T, TPayload, TResult>>) {


    this.commandQueue = new CommandQueue('store-command-executor')
  }

  public async start() {
    this.commandQueue.start()
  }

  private async executeCommand(command: Command) {
    debug('executing command', command.name, command.payload);
    const handler = this.handlerFactory(command.name);
    await handler.execute(command.payload);
    debug('finishing command', command.name);
  }

  public async finish() {
    debug('stopping store command executor...')
    await this.commandQueue.stop()
    debug('store command executor stopped')
  }

  public queue(command: Command) {

    const frozen = Object.freeze(command)

    this.commandQueue.queue('command', async (_cancel) => {
      await this.executeCommand(frozen)
    })
  }

  public async query<T extends string, TPayload, TResult>(query: Query<T, TPayload, TResult>): Promise<TResult> {

    const self = this
    
    const frozen = Object.freeze(query)

    debug('query queueAndWait', query.name, query.payload);

    const result = await this.commandQueue.queueAndWait<TResult>('query-queue-and-wait', async (_cancel) => {
      const result = await self.executeQuery(frozen) as TResult
      return result
    })

    debug('query finished', query.name, result);
    return result
  }

  private async executeQuery<T extends string, TPayload, TResult>(
    query: Query<T, TPayload, TResult>): Promise<TResult> {

    debug('executing query', query.name, query.payload);
    const handler = this.queryHandlerFactory<T, TPayload, TResult>(query.name)
    const payload: TPayload = query.payload
    const result = await handler.execute(payload as any);
    debug('finishing query', query.name, result);

    return result
  }
}
